import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiUrl, type ChatStatus, type Connection, type Settings } from "../lib/api";
import { ChatFeed } from "./ChatFeed";
import { InfoTip } from "./InfoTip";

type Platform = "twitch" | "youtube";

/**
 * The Settings tab is a stack of single-concern cards. This module owns:
 *   • Requests status — the open/closed switch for new requests. Kept as its
 *                        own card, first, since it's the one toggle a streamer
 *                        needs to find fast mid-stream.
 *   • Queue settings  — paid threshold, ordering strategies, per-viewer cap.
 *   • Chat commands   — the trigger words + the bot's reply templates.
 *   • Donation bot    — the trusted Streamlabs/StreamElements chat account to
 *                        parse tips from, in Twitch chat only (see docs/chat-ingestion.md).
 *   • Twitch subs     — flat credit amounts for a new sub / gift sub. Grouped
 *                        right after Donation bot since both are Twitch-only
 *                        money paths.
 *   • YT memberships  — flat credit amount for a new YouTube channel member.
 *   • Chat connection — the live connect/disconnect toggle (streamer-only).
 *   • Chat feed       — live preview of what the bot is seeing/matching.
 * Mods + Broadcast live in ModsPanel. Each concern is its own `.panel` — no
 * divider-in-a-card any more.
 */
export function SettingsPanel({
  channelId,
  settings,
  isStreamer,
  onSaved,
}: {
  channelId: string;
  settings: Settings;
  /** Only the channel owner may toggle chat ingestion. */
  isStreamer: boolean;
  onSaved: () => void;
}) {
  return (
    <>
      <RequestsStatus channelId={channelId} settings={settings} onSaved={onSaved} />
      <QueueSettings channelId={channelId} settings={settings} onSaved={onSaved} />
      <ChatCommands channelId={channelId} settings={settings} onSaved={onSaved} />
      <DonationBotSettings channelId={channelId} settings={settings} onSaved={onSaved} />
      <TwitchSubSettings channelId={channelId} settings={settings} onSaved={onSaved} />
      <YouTubeMembershipSettings channelId={channelId} settings={settings} onSaved={onSaved} />
      <ChatConnection channelId={channelId} settings={settings} isStreamer={isStreamer} />
      <ChatFeed channelId={channelId} />
    </>
  );
}

/**
 * Open/closed switch for new requests — the one toggle that needs to be
 * unmissable, so it's its own card at the top rather than a checkbox buried
 * in Chat commands. The status flips immediately on click (like
 * `ChatConnection`'s connect/disconnect) rather than sitting in a draft
 * behind a Save button; the reply template below is still its own
 * save-on-edit field since it's low-stakes copy, not a live toggle.
 */
function RequestsStatus({
  channelId,
  settings,
  onSaved,
}: {
  channelId: string;
  settings: Settings;
  onSaved: () => void;
}) {
  const enabled = settings.requestsEnabled;
  const closedReply = settings.requestsClosedReplyTemplate;

  const [draftClosedReply, setDraftClosedReply] = useState(closedReply);
  const [toggling, setToggling] = useState(false);
  const [savingReply, setSavingReply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraftClosedReply(closedReply), [closedReply]);
  const replyDirty = draftClosedReply !== closedReply;

  async function toggle() {
    setToggling(true);
    setError(null);
    try {
      await api.putSettings({ channelId, requestsEnabled: !enabled });
      onSaved();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setToggling(false);
    }
  }

  async function saveReply() {
    setSavingReply(true);
    setError(null);
    try {
      await api.putSettings({ channelId, requestsClosedReplyTemplate: draftClosedReply.trim() });
      onSaved();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSavingReply(false);
    }
  }

  return (
    <div className="panel">
      <h2>Requests</h2>
      <div className="connection-row">
        <span className={`live ${enabled ? "on" : ""}`} style={{ fontSize: 14 }}>
          <span className="dot" />
          {enabled ? "Open — viewers can add requests" : "Closed to new requests"}
        </span>
        <div style={{ flex: 1 }} />
        <button
          className={`btn ${enabled ? "" : "primary"} row-action`}
          onClick={toggle}
          disabled={toggling}
        >
          {toggling ? "Saving…" : enabled ? "Close requests" : "Open requests"}
        </button>
      </div>

      <div className="row" style={{ marginTop: 14 }}>
        <div className="field grow">
          <div className="label-row">
            <label htmlFor="requestsClosedReplyTemplate">Requests closed reply</label>
            <InfoTip text="What the bot sends in chat instead of queueing, while requests are closed." />
          </div>
          <textarea
            id="requestsClosedReplyTemplate"
            value={draftClosedReply}
            placeholder="@{user} requests are closed right now — check back later!"
            spellCheck={false}
            rows={2}
            onChange={(e) => setDraftClosedReply(e.target.value)}
          />
        </div>
        <button
          className="btn primary row-action"
          onClick={saveReply}
          disabled={!replyDirty || savingReply}
        >
          {savingReply ? "Saving…" : "Save"}
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
      <p className="muted panel-hint">
        Only the request command is gated — manual adds from the Queue tab, the position
        command, and donations keep working either way. Placeholder: <code>{"{user}"}</code>.
      </p>
    </div>
  );
}

/**
 * Paid threshold + ordering strategies. Saving re-reconciles the stream
 * server-side (items can move between paid and free, and positions renumber), so
 * the queue refreshes right after. The chat command deliberately lives with chat
 * ingestion below, not here.
 */
function QueueSettings({
  channelId,
  settings,
  onSaved,
}: {
  channelId: string;
  settings: Settings;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Settings>(settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reflect external changes (another admin, or a reconcile).
  useEffect(() => setDraft(settings), [settings]);

  const dirty =
    draft.paidThresholdCents !== settings.paidThresholdCents ||
    draft.paidOrdering !== settings.paidOrdering ||
    draft.freeOrdering !== settings.freeOrdering ||
    draft.maxRequestsPerUser !== settings.maxRequestsPerUser ||
    draft.maxQueueRequests !== settings.maxQueueRequests ||
    draft.freeVideoLimitSeconds !== settings.freeVideoLimitSeconds ||
    draft.nowPlayingPlaybackEnabled !== settings.nowPlayingPlaybackEnabled;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Explicit patch — never send chatIngestEnabled (connect/disconnect owns
      // it); chatCommand is saved from the Chat ingestion card.
      await api.putSettings({
        channelId,
        paidThresholdCents: draft.paidThresholdCents,
        paidOrdering: draft.paidOrdering,
        freeOrdering: draft.freeOrdering,
        maxRequestsPerUser: draft.maxRequestsPerUser,
        maxQueueRequests: draft.maxQueueRequests,
        freeVideoLimitSeconds: draft.freeVideoLimitSeconds,
        nowPlayingPlaybackEnabled: draft.nowPlayingPlaybackEnabled,
      });
      onSaved();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel">
      <h2>Queue settings</h2>
      <div className="row">
        <div className="field" style={{ flex: "0 0 140px" }}>
          <label htmlFor="threshold">Paid threshold ($)</label>
          <input
            id="threshold"
            type="number"
            min="0"
            step="0.01"
            value={(draft.paidThresholdCents / 100).toFixed(2)}
            onChange={(e) =>
              setDraft({
                ...draft,
                paidThresholdCents: Math.max(
                  0,
                  Math.round(Number(e.target.value || 0) * 100),
                ),
              })
            }
          />
        </div>

        <div className="field" style={{ flex: "0 1 210px" }}>
          <div className="label-row">
            <label htmlFor="paidOrdering">Paid ordering</label>
            <InfoTip text="Saving re-sorts the queue. Pinned items keep their slot regardless of ordering." />
          </div>
          <select
            id="paidOrdering"
            value={draft.paidOrdering}
            onChange={(e) =>
              setDraft({ ...draft, paidOrdering: e.target.value as Settings["paidOrdering"] })
            }
          >
            <option value="fifo">First in, first out</option>
            <option value="newest_first">Newest first</option>
            <option value="amount_desc">Highest tip first</option>
          </select>
        </div>

        <div className="field" style={{ flex: "0 1 210px" }}>
          <label htmlFor="freeOrdering">Free ordering</label>
          <select
            id="freeOrdering"
            value={draft.freeOrdering}
            onChange={(e) =>
              setDraft({ ...draft, freeOrdering: e.target.value as Settings["freeOrdering"] })
            }
          >
            <option value="fifo">First in, first out</option>
            <option value="newest_first">Newest first</option>
          </select>
        </div>

        <div className="field" style={{ flex: "0 0 160px" }}>
          <div className="label-row">
            <label htmlFor="maxRequestsPerUser">Max free requests per viewer</label>
            <InfoTip text="A viewer at this cap can't add another free request until one of theirs plays or is removed. Paid requests are never limited." />
          </div>
          <input
            id="maxRequestsPerUser"
            type="number"
            min="1"
            step="1"
            value={draft.maxRequestsPerUser}
            onChange={(e) =>
              setDraft({
                ...draft,
                maxRequestsPerUser: Math.max(1, Math.round(Number(e.target.value || 1))),
              })
            }
          />
        </div>

        <div className="field" style={{ flex: "0 0 160px" }}>
          <div className="label-row">
            <label htmlFor="maxQueueRequests">Max queue size</label>
            <InfoTip text="Total active (paid + free) requests the queue can hold at once. A viewer whose request would land free gets the 'queue full' reply below instead. Paid requests are never limited. 0 = unlimited." />
          </div>
          <input
            id="maxQueueRequests"
            type="number"
            min="0"
            step="1"
            value={draft.maxQueueRequests}
            onChange={(e) =>
              setDraft({
                ...draft,
                maxQueueRequests: Math.max(0, Math.round(Number(e.target.value || 0))),
              })
            }
          />
        </div>

        <div className="field" style={{ flex: "0 0 160px" }}>
          <div className="label-row">
            <label htmlFor="freeVideoLimitSeconds">Free video limit (min)</label>
            <InfoTip text="Longest video the chat request command will queue for free — an over-limit video gets the 'too long' reply (see Chat commands below) instead of being queued. Only checked when a YouTube API key is configured. Paid requests are exempt." />
          </div>
          <input
            id="freeVideoLimitSeconds"
            type="number"
            min="1"
            max="60"
            step="1"
            value={Math.round(draft.freeVideoLimitSeconds / 60)}
            onChange={(e) =>
              setDraft({
                ...draft,
                freeVideoLimitSeconds:
                  Math.min(60, Math.max(1, Math.round(Number(e.target.value || 1)))) * 60,
              })
            }
          />
        </div>

        <button className="btn primary row-action" onClick={save} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="field" style={{ marginTop: 12 }}>
        <div className="row" style={{ alignItems: "center", gap: 8 }}>
          <label className="row" style={{ alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={draft.nowPlayingPlaybackEnabled}
              onChange={(e) =>
                setDraft({ ...draft, nowPlayingPlaybackEnabled: e.target.checked })
              }
            />
            Show play/pause button on the now-playing card
          </label>
          <InfoTip text="Remote-controls the video embed on your OBS overlay." />
        </div>
      </div>
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  );
}

/**
 * Chat commands: the trigger words (`chatCommand`/`positionCommand`) and the
 * bot's reply templates for each. One concern — "what makes the bot react,
 * and what it says back" — saved together with a single button. Connecting
 * the chat itself lives in `ChatConnection` below.
 */
function ChatCommands({
  channelId,
  settings,
  onSaved,
}: {
  channelId: string;
  settings: Settings;
  onSaved: () => void;
}) {
  const command = settings.chatCommand;
  const posCommand = settings.positionCommand;
  const vrReply = settings.vrReplyTemplate;
  const posReply = settings.posReplyTemplate;
  const maxReachedReply = settings.maxReachedReplyTemplate;
  const queueFullReply = settings.queueFullReplyTemplate;
  const videoTooLongReply = settings.videoTooLongReplyTemplate;

  const [draftCommand, setDraftCommand] = useState(command);
  const [draftPosCommand, setDraftPosCommand] = useState(posCommand);
  const [draftVrReply, setDraftVrReply] = useState(vrReply);
  const [draftPosReply, setDraftPosReply] = useState(posReply);
  const [draftMaxReachedReply, setDraftMaxReachedReply] = useState(maxReachedReply);
  const [draftQueueFullReply, setDraftQueueFullReply] = useState(queueFullReply);
  const [draftVideoTooLongReply, setDraftVideoTooLongReply] = useState(videoTooLongReply);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraftCommand(command), [command]);
  useEffect(() => setDraftPosCommand(posCommand), [posCommand]);
  useEffect(() => setDraftVrReply(vrReply), [vrReply]);
  useEffect(() => setDraftPosReply(posReply), [posReply]);
  useEffect(() => setDraftMaxReachedReply(maxReachedReply), [maxReachedReply]);
  useEffect(() => setDraftQueueFullReply(queueFullReply), [queueFullReply]);
  useEffect(() => setDraftVideoTooLongReply(videoTooLongReply), [videoTooLongReply]);
  const dirty =
    draftCommand !== command ||
    draftPosCommand !== posCommand ||
    draftVrReply !== vrReply ||
    draftPosReply !== posReply ||
    draftMaxReachedReply !== maxReachedReply ||
    draftQueueFullReply !== queueFullReply ||
    draftVideoTooLongReply !== videoTooLongReply;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.putSettings({
        channelId,
        chatCommand: draftCommand.trim(),
        positionCommand: draftPosCommand.trim(),
        vrReplyTemplate: draftVrReply.trim(),
        posReplyTemplate: draftPosReply.trim(),
        maxReachedReplyTemplate: draftMaxReachedReply.trim(),
        queueFullReplyTemplate: draftQueueFullReply.trim(),
        videoTooLongReplyTemplate: draftVideoTooLongReply.trim(),
      });
      onSaved();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel">
      <h2>Chat commands</h2>
      <p className="muted panel-lead">
        Viewers who type <code>{command || "your command"}</code> &lt;item&gt; in chat
        add a free request. <code>{posCommand || "your position command"}</code> replies
        with their spot in the queue.
      </p>

      <p className="panel-subhead">Triggers</p>
      <div className="row">
        <div className="field grow">
          <label htmlFor="chatCommand">Request command</label>
          <input
            id="chatCommand"
            value={draftCommand}
            placeholder="!req"
            spellCheck={false}
            onChange={(e) => setDraftCommand(e.target.value)}
          />
        </div>
        <div className="field grow">
          <label htmlFor="positionCommand">Position command</label>
          <input
            id="positionCommand"
            value={draftPosCommand}
            placeholder="!pos"
            spellCheck={false}
            onChange={(e) => setDraftPosCommand(e.target.value)}
          />
        </div>
      </div>

      <p className="panel-subhead" style={{ marginTop: 16 }}>
        Bot replies
      </p>
      <div className="row">
        <div className="field grow">
          <label htmlFor="vrReplyTemplate">Request reply</label>
          <textarea
            id="vrReplyTemplate"
            value={draftVrReply}
            placeholder="@{user} request added to the queue!"
            spellCheck={false}
            rows={5}
            onChange={(e) => setDraftVrReply(e.target.value)}
          />
        </div>
        <div className="field grow">
          <label htmlFor="posReplyTemplate">Position reply</label>
          <textarea
            id="posReplyTemplate"
            value={draftPosReply}
            placeholder='@{user} "{title}" is #{position} in the queue ({ahead}){more}.'
            spellCheck={false}
            rows={5}
            onChange={(e) => setDraftPosReply(e.target.value)}
          />
        </div>
        <div className="field grow">
          <div className="label-row">
            <label htmlFor="maxReachedReplyTemplate">Max requests reached reply</label>
            <InfoTip text="Sent instead of the request reply when the chatter is already at the max free requests per viewer cap set above and tries to add another." />
          </div>
          <textarea
            id="maxReachedReplyTemplate"
            value={draftMaxReachedReply}
            placeholder="@{user} you already have {limit} requests queued — wait for one to play before adding more."
            spellCheck={false}
            rows={5}
            onChange={(e) => setDraftMaxReachedReply(e.target.value)}
          />
        </div>
        <div className="field grow">
          <div className="label-row">
            <label htmlFor="queueFullReplyTemplate">Queue full reply</label>
            <InfoTip text="Sent instead of the request reply when the queue is at the max queue size cap set above and the request would land free." />
          </div>
          <textarea
            id="queueFullReplyTemplate"
            value={draftQueueFullReply}
            placeholder="@{user} the queue is full right now (max {limit}) — try again later!"
            spellCheck={false}
            rows={5}
            onChange={(e) => setDraftQueueFullReply(e.target.value)}
          />
        </div>
        <div className="field grow">
          <div className="label-row">
            <label htmlFor="videoTooLongReplyTemplate">Video too long reply</label>
            <InfoTip text="Sent instead of queueing when a requested video is over the free video limit set in Queue settings above." />
          </div>
          <textarea
            id="videoTooLongReplyTemplate"
            value={draftVideoTooLongReply}
            placeholder="@{user} free requests are capped at {limit} min — donate to unlock requests 7+ minutes long!"
            spellCheck={false}
            rows={5}
            onChange={(e) => setDraftVideoTooLongReply(e.target.value)}
          />
        </div>
        <button className="btn primary row-action" onClick={save} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
      <p className="muted panel-hint">
        Placeholders: <code>{"{user}"}</code> everywhere;{" "}
        <code>{"{title}"}</code>, <code>{"{position}"}</code>, <code>{"{ahead}"}</code> in the
        position reply only; <code>{"{limit}"}</code> in the max-requests, queue-full, and
        video-too-long replies (minutes, in the last one).
      </p>
    </div>
  );
}

/** The exact chat-alert template the streamer must set in their bot's dashboard. */
const DONATION_TEMPLATES: Record<Settings["donationBotProvider"], string> = {
  streamlabs: "{user.name} donated {amount}",
  streamelements: "{user} tipped {amount}",
};

/** The chat username each provider's alert bot posts under, out of the box. */
const DEFAULT_BOT_USERNAMES: Record<Settings["donationBotProvider"], string> = {
  streamlabs: "Streamlabs",
  streamelements: "StreamElements",
};

/**
 * Twitch donation-bot chat scanning: which provider's alert format to expect,
 * and the trusted chat account it must come from — anything else is ignored,
 * since this moves real money/credit. Twitch-only (see `classify` in
 * api/src/lib/chat/ingest.ts) since the trusted bot account is assumed to
 * post its alerts in Twitch chat, not YouTube's. Rides the same live chat
 * feed as chat commands, but is otherwise an independent concern (empty
 * username ⇒ off).
 */
function DonationBotSettings({
  channelId,
  settings,
  onSaved,
}: {
  channelId: string;
  settings: Settings;
  onSaved: () => void;
}) {
  const provider = settings.donationBotProvider;
  const username = settings.donationBotUsername;

  const [draftProvider, setDraftProvider] = useState(provider);
  const [draftUsername, setDraftUsername] = useState(username);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraftProvider(provider), [provider]);
  useEffect(() => setDraftUsername(username), [username]);
  const dirty = draftProvider !== provider || draftUsername !== username;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.putSettings({
        channelId,
        donationBotProvider: draftProvider,
        donationBotUsername: draftUsername.trim(),
      });
      onSaved();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel">
      <h2>Twitch donation bot</h2>
      <p className="muted panel-lead">
        Detects tips from your donation bot's own alerts in Twitch chat — a second way
        donations reach the queue, alongside the manual/API path. Only messages from the
        chat account below are ever treated as money.
      </p>

      <div className="row">
        <div className="field grow">
          <div className="label-row">
            <label htmlFor="donationBotProvider">Alert format</label>
            <InfoTip text="Which alert message format to parse — Streamlabs or StreamElements. Your bot's dashboard must be set to post donation alerts in that exact wording (see hint below)." />
          </div>
          <select
            id="donationBotProvider"
            value={draftProvider}
            onChange={(e) => {
              const next = e.target.value as Settings["donationBotProvider"];
              // Follow the provider switch with its own default bot account,
              // but only when the field still holds the *previous* provider's
              // default untouched — never clobber a username the streamer typed,
              // and never turn on an intentionally-blank (disabled) field.
              if (draftUsername === DEFAULT_BOT_USERNAMES[draftProvider]) {
                setDraftUsername(DEFAULT_BOT_USERNAMES[next]);
              }
              setDraftProvider(next);
            }}
          >
            <option value="streamlabs">Streamlabs</option>
            <option value="streamelements">StreamElements</option>
          </select>
        </div>
        <div className="field grow">
          <div className="label-row">
            <label htmlFor="donationBotUsername">Trusted Twitch chat username</label>
            <InfoTip text="The trusted Twitch chat account whose messages are checked for donation alerts. Messages from any other account — or from YouTube chat — are ignored, since this moves real money. Leave blank to turn off donation-bot detection." />
          </div>
          <input
            id="donationBotUsername"
            value={draftUsername}
            placeholder={DEFAULT_BOT_USERNAMES[draftProvider]}
            spellCheck={false}
            onChange={(e) => setDraftUsername(e.target.value)}
          />
        </div>
        <button className="btn primary row-action" onClick={save} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
      <p className="muted panel-hint">
        Set your {draftProvider === "streamlabs" ? "Streamlabs Cloudbot" : "StreamElements Chat Alerts"}{" "}
        donation message to exactly <code>{DONATION_TEMPLATES[draftProvider]}</code> so it parses.
      </p>
    </div>
  );
}

/**
 * YouTube membership crediting (issue #44), similar in spirit to the
 * donation bot card but a separate concern: it's platform-attested (the
 * chat crawler itself sees the "new member" event) rather than parsed off a
 * trusted bot account, and it's YouTube-only. $0 turns it off.
 */
function YouTubeMembershipSettings({
  channelId,
  settings,
  onSaved,
}: {
  channelId: string;
  settings: Settings;
  onSaved: () => void;
}) {
  const cents = settings.membershipDonationCents;
  const [draftCents, setDraftCents] = useState(cents);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraftCents(cents), [cents]);
  const dirty = draftCents !== cents;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.putSettings({ channelId, membershipDonationCents: draftCents });
      onSaved();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel">
      <h2>YouTube memberships</h2>
      <p className="muted panel-lead">
        Credits a chatter when the crawler sees them become a new YouTube channel
        member, same as a tip of this amount landing in chat. Set to $0 to turn off.
      </p>

      <div className="row">
        <div className="field" style={{ flex: "0 0 140px" }}>
          <div className="label-row">
            <label htmlFor="membershipAmount">Credit per join ($)</label>
            <InfoTip text="Flat amount credited to a chatter's donor credit when they join as a YouTube member — YouTube doesn't report what they actually paid, so this is a fixed value you set. $0 disables membership crediting." />
          </div>
          <input
            id="membershipAmount"
            type="number"
            min="0"
            step="0.01"
            value={(draftCents / 100).toFixed(2)}
            onChange={(e) =>
              setDraftCents(Math.max(0, Math.round(Number(e.target.value || 0) * 100)))
            }
          />
        </div>
        <button className="btn primary row-action" onClick={save} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  );
}

/**
 * Twitch sub/gift-sub crediting (issue #42). Two amounts — subs and gift subs
 * are separate EventSub events, credited to different people (the subscriber
 * vs. the gifter) and worth setting independently. Not tier-aware: Twitch's
 * own EventSub payload can't distinguish a Prime sub from a paid one, so
 * every non-gift sub is credited this same flat amount. $0 turns each half
 * off independently.
 */
function TwitchSubSettings({
  channelId,
  settings,
  onSaved,
}: {
  channelId: string;
  settings: Settings;
  onSaved: () => void;
}) {
  const subCents = settings.twitchSubDonationCents;
  const giftCents = settings.twitchGiftSubDonationCents;
  const [draftSubCents, setDraftSubCents] = useState(subCents);
  const [draftGiftCents, setDraftGiftCents] = useState(giftCents);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraftSubCents(subCents), [subCents]);
  useEffect(() => setDraftGiftCents(giftCents), [giftCents]);
  const dirty = draftSubCents !== subCents || draftGiftCents !== giftCents;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.putSettings({
        channelId,
        twitchSubDonationCents: draftSubCents,
        twitchGiftSubDonationCents: draftGiftCents,
      });
      onSaved();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel">
      <h2>Twitch subs</h2>
      <p className="muted panel-lead">
        Credits a viewer when the crawler sees them subscribe, and credits a gifter for
        each sub they gift, same as a tip of these amounts landing in chat. Not
        tier-aware — Prime and paid subs are credited the same, since Twitch doesn't
        report which one it was. Set an amount to $0 to turn that half off.
      </p>

      <div className="row">
        <div className="field" style={{ flex: "0 0 140px" }}>
          <div className="label-row">
            <label htmlFor="twitchSubAmount">Credit per sub ($)</label>
            <InfoTip text="Flat amount credited to a subscriber's donor credit — Twitch doesn't report what they actually paid (and can't tell a Prime sub from a paid one), so this is a fixed value you set. Gift-recipient subs are excluded — that money is credited to the gifter instead. $0 disables sub crediting." />
          </div>
          <input
            id="twitchSubAmount"
            type="number"
            min="0"
            step="0.01"
            value={(draftSubCents / 100).toFixed(2)}
            onChange={(e) =>
              setDraftSubCents(Math.max(0, Math.round(Number(e.target.value || 0) * 100)))
            }
          />
        </div>
        <div className="field" style={{ flex: "0 0 140px" }}>
          <div className="label-row">
            <label htmlFor="twitchGiftSubAmount">Credit per gift sub ($)</label>
            <InfoTip text="Flat amount credited to the gifter's donor credit, per sub they gift — a gift of 5 subs credits 5× this amount, all to the gifter (not the recipients). $0 disables gift-sub crediting." />
          </div>
          <input
            id="twitchGiftSubAmount"
            type="number"
            min="0"
            step="0.01"
            value={(draftGiftCents / 100).toFixed(2)}
            onChange={(e) =>
              setDraftGiftCents(Math.max(0, Math.round(Number(e.target.value || 0) * 100)))
            }
          />
        </div>
        <button className="btn primary row-action" onClick={save} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  );
}

const CHAT_PLATFORMS: Array<{ id: Platform; label: string }> = [
  { id: "twitch", label: "Twitch" },
  { id: "youtube", label: "YouTube" },
];

/**
 * Chat connection: independent connect/disconnect toggles per platform, since
 * a channel can bind BOTH at once (Quelby's whole premise is simulcasting to
 * Twitch + YouTube together) — Twitch via EventSub, YouTube via the poller.
 * Gated on `settings.chatCommand` already being saved — a command edited but
 * not yet saved can't connect.
 *
 * Connecting always goes through the OAuth authorize flow first (`?link=1`,
 * so it refreshes/attaches that platform's own identity without disturbing
 * whichever identity the caller is currently logged in as), with `?scope=chat`
 * so the stored token is guaranteed to carry the chat scope — this is the one
 * flow in the app that's allowed to request YouTube's elevated scope (see
 * getProviderConfig in oauth/providers.ts); everywhere else only asks for
 * identity. `/auth/<platform>` is told to return to `/settings?connect=<platform>`;
 * on the way back we auto-complete that platform's bind. Disconnect is a plain
 * API call (no re-auth needed).
 */
function ChatConnection({
  channelId,
  settings,
  isStreamer,
}: {
  channelId: string;
  settings: Settings;
  isStreamer: boolean;
}) {
  const command = settings.chatCommand;

  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [busy, setBusy] = useState<Platform | null>(null);
  const [error, setError] = useState<string | null>(null);
  const autoRan = useRef(false);

  useEffect(() => {
    api.me().then((m) => setConnections(m.connections)).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    try {
      setStatus(await api.chatStatus(channelId));
    } catch {
      /* status is best-effort; leave it null */
    }
  }, [channelId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const bind = useCallback(
    async (platform: Platform) => {
      setBusy(platform);
      setError(null);
      try {
        await api.chatConnect(channelId, platform);
        await refresh();
      } catch (err) {
        setError(String(err instanceof Error ? err.message : err));
      } finally {
        setBusy(null);
      }
    },
    [channelId, refresh],
  );

  // Landed back from the OAuth authorize flow — finish that platform's bind,
  // then drop the ?connect flag so a refresh doesn't reconnect. Streamer-only.
  useEffect(() => {
    if (autoRan.current || !isStreamer) return;
    const params = new URLSearchParams(window.location.search);
    const platform = params.get("connect");
    if (platform !== "twitch" && platform !== "youtube") return;
    autoRan.current = true;
    params.delete("connect");
    const rest = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
    void bind(platform);
    // `isStreamer` is in the deps so this re-runs once the role resolves — the
    // guard above returns early until then without consuming `?connect=`.
  }, [bind, isStreamer]);

  async function disconnect(platform: Platform) {
    setBusy(platform);
    setError(null);
    try {
      await api.chatDisconnect(channelId, platform);
      await refresh();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setBusy(null);
    }
  }

  function authorizeAndConnect(platform: Platform) {
    // link=1 refreshes/attaches this platform's own identity without switching
    // which identity the session is logged in as; scope=chat requests the
    // elevated (chat-capable) scope on top of that. The callback returns to
    // Settings with ?connect=<platform>, which the effect above completes.
    const returnTo = encodeURIComponent(`/settings?connect=${platform}`);
    window.location.href = apiUrl(
      `/auth/${platform}?link=1&scope=chat&returnTo=${returnTo}`,
    );
  }

  const needsCommand = !command;

  return (
    <div className="panel">
      <h2>Chat connection</h2>
      {needsCommand && isStreamer && (
        <p className="muted panel-hint">Set a chat command above before connecting.</p>
      )}
      <ul className="connections">
        {CHAT_PLATFORMS.map(({ id, label }) => {
          const linked = Boolean(connections.find((c) => c.platform === id)?.connected);
          const platformStatus = status?.platforms[id];
          const connected = Boolean(platformStatus?.connected);
          const pending = Boolean(platformStatus?.pending);

          return (
            <li key={id} className="connection-row">
              <span className={`badge platform ${id}`}>{label}</span>
              <div className="connection-detail">
                <span className={`live ${connected ? "on" : ""}`}>
                  <span className="dot" />
                  {connected
                    ? "chat connected"
                    : pending
                      ? "waiting for you to go live…"
                      : "not connected"}
                </span>
                {!connected && !linked && (
                  <span className="connection-warn">Link {label} in Connections first.</span>
                )}
                {!connected && id === "youtube" && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    You need to be live on YouTube before connecting — Connect looks for an
                    active broadcast to attach to.
                  </span>
                )}
                {connected && id === "twitch" && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    Twitch throttles unfamiliar bots — type <code>/mod quelbybot</code> in your
                    chat if replies aren't landing.
                  </span>
                )}
              </div>
              {isStreamer ? (
                <button
                  className={`btn ${connected ? "danger" : "primary"}`}
                  onClick={() => (connected ? disconnect(id) : authorizeAndConnect(id))}
                  disabled={busy !== null || (!connected && (!command || !status))}
                >
                  {busy === id ? "…" : connected ? "Disconnect" : pending ? "Retry" : "Connect"}
                </button>
              ) : (
                <span className="muted" style={{ fontSize: 13 }}>
                  only the streamer can toggle this
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  );
}
